import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSelectedEmployee } from '../context/SelectedEmployeeContext';
import Icon from './Icons';
import EmployeeDropdown from './EmployeeDropdown';
import AppModal from './AppModal';
import api, { getApiMessage } from '../utils/api';
import './Header.css';

const pageMeta = {
  '/home': { title: 'Dashboard', breadcrumb: ['Dashboard'] },
  '/salary-processing': { title: 'Salary Processing', breadcrumb: ['Dashboard', 'Salary Processing'] },
  '/change-log': { title: 'Change Log', breadcrumb: ['Dashboard', 'Change Log'] },
  '/profile': { title: 'My Profile', breadcrumb: ['Dashboard', 'My Profile'] },
  '/leaves': { title: 'Leave Management', breadcrumb: ['Dashboard', 'Leave Management'] },
  '/salary': { title: 'Salary', breadcrumb: ['Dashboard', 'Salary'] },
  '/bonus': { title: 'Bonus', breadcrumb: ['Dashboard', 'Bonus'] },
};

export default function Header() {
  const { user, logout, isAdmin, isSuperAdmin, roleLabel } = useAuth();
  const { selectedEmployee, clearSelection } = useSelectedEmployee();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showChangePw, setShowChangePw] = useState(false);
  const [pwForm, setPwForm] = useState({ new_password: '', confirm_password: '' });
  const [pwError, setPwError] = useState('');
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwVisible, setPwVisible] = useState({ new: false, confirm: false });
  const [pwTarget, setPwTarget] = useState(null); // { username, label } — locked at open time
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);

  const superAdminNavItems = [
    { path: '/home', label: 'Dashboard', icon: 'home' },
    { path: '/salary-processing', label: 'Salary Processing', icon: 'clipboardList' },
    { path: '/change-log', label: 'Change Log', icon: 'fileText' },
  ];
  const commonNavItems = [
    { path: '/profile', label: 'My Profile', icon: 'user' },
    { path: '/leaves', label: 'Leave Management', icon: 'calendar' },
    { path: '/salary', label: 'Salary', icon: 'salary' },
    { path: '/bonus', label: 'Bonus', icon: 'bonus' },
  ];

  const handleLogout = () => {
    logout();
    window.location.replace('/login');
  };

  const handleChangePw = async (e) => {
    e.preventDefault();
    setPwError('');

    const { new_password, confirm_password } = pwForm;
    if (!new_password || !confirm_password) {
      setPwError('Password is required.');
      return;
    }
    if (new_password.length < 10) {
      setPwError('Password must be at least 10 characters.');
      return;
    }
    if (!/[A-Z]/.test(new_password) || !/[a-z]/.test(new_password) || !/[0-9]/.test(new_password) || !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(new_password)) {
      setPwError('Password must include uppercase, lowercase, number, and special character.');
      return;
    }
    if (new_password !== confirm_password) {
      setPwError('Passwords do not match.');
      return;
    }

    setPwSaving(true);
    try {
      const payload = { new_password, confirm_password };
      if (pwTarget?.username) payload.target_username = pwTarget.username;
      await api.put('/profile/change-password', payload);
      setPwForm({ new_password: '', confirm_password: '' });
      setTimeout(() => setShowChangePw(false), 1200);
      setPwSuccess('Password changed successfully.');
    } catch (err) {
      setPwError(getApiMessage(err, 'Failed to change password.'));
    } finally {
      setPwSaving(false);
    }
  };

  const openChangePw = () => {
    // Determine target: on /home always self; on other pages use selected employee if any
    const isHomePage = location.pathname === '/home';
    const target = (!isHomePage && isAdmin && selectedEmployee)
      ? { username: selectedEmployee.username, label: `${selectedEmployee.first_name || ''} ${selectedEmployee.last_name || ''}`.trim() || selectedEmployee.username }
      : null; // null = logged-in user (self)
    setPwTarget(target);
    setShowChangePw(true);
    setPwForm({ new_password: '', confirm_password: '' });
    setPwError('');
    setPwSuccess('');
    setPwVisible({ new: false, confirm: false });
    setUserMenuOpen(false);
    setDrawerOpen(false);
  };

  const handleNav = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        setUserMenuOpen(false);
      }
    };
    if (drawerOpen || userMenuOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [drawerOpen, userMenuOpen]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const inDesktop = userMenuRef.current && userMenuRef.current.contains(e.target);
      const inMobile = mobileMenuRef.current && mobileMenuRef.current.contains(e.target);
      if (!inDesktop && !inMobile) setUserMenuOpen(false);
    };
    if (userMenuOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [drawerOpen]);

  const initials = user?.first_name
    ? `${user.first_name[0]}${user.last_name?.[0] || ''}`.toUpperCase()
    : user?.username?.[0]?.toUpperCase() || 'U';

  const fullName = `${user?.first_name || user?.username || ''}${user?.last_name ? ` ${user.last_name}` : ''}`;

  const currentPage = pageMeta[location.pathname] || { title: 'Dashboard', breadcrumb: ['Dashboard'] };

  return (
    <>
      {/* Mobile Top Bar */}
      <header className="mobile-topbar">
        <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="Open menu">
          <Icon name="menu" size={22} />
        </button>
        <div className="mobile-topbar-brand">
          <img src="/logo.jpg" alt="Tulsian Group" className="mobile-logo-img" />
        </div>
        <div className="mobile-user-wrapper" ref={mobileMenuRef}>
          <div className="mobile-topbar-avatar" onClick={() => setUserMenuOpen((p) => !p)}>
            <span>{initials}</span>
          </div>
          {userMenuOpen && (
            <div className="user-dropdown mobile-user-dropdown">
              <div className="user-dropdown-header">
                <div className="user-dropdown-avatar">{initials}</div>
                <div className="user-dropdown-details">
                  <span className="user-dropdown-name">{fullName}</span>
                  <span className="user-dropdown-email">{user?.company_email || user?.personal_email || ''}</span>
                </div>
              </div>
              <div className="user-dropdown-divider" />
              <button className="user-dropdown-item" onClick={() => { setUserMenuOpen(false); clearSelection(); navigate('/profile'); }}>
                <Icon name="user" size={18} />
                <span>View Profile</span>
              </button>
              <button className="user-dropdown-item" onClick={openChangePw}>
                <Icon name="lock" size={18} />
                <span>Change Password</span>
              </button>
              <div className="user-dropdown-divider" />
              <button className="user-dropdown-item user-dropdown-logout" onClick={() => { setUserMenuOpen(false); handleLogout(); }}>
                <Icon name="logout" size={18} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Desktop Top Bar */}
      <div className="desktop-topbar">
        <div className="topbar-page-info">
          <h1 className="topbar-page-title">{currentPage.title}</h1>
          <div className="topbar-breadcrumb">
            {currentPage.breadcrumb.map((crumb, i) => (
              <React.Fragment key={i}>
                {i > 0 && <span className="topbar-breadcrumb-sep">&gt;</span>}
                <span className={`topbar-breadcrumb-item ${i === currentPage.breadcrumb.length - 1 ? 'active' : ''}`}>
                  {crumb}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
        <EmployeeDropdown />
      </div>

      {/* Drawer Overlay */}
      {drawerOpen && (
        <div className="drawer-overlay" onClick={() => setDrawerOpen(false)} />
      )}

      {/* Sidebar / Drawer */}
      <aside className={`sidebar ${drawerOpen ? 'sidebar--open' : ''}`} role="navigation" aria-label="Main navigation">
        <div className="sidebar-header">
          <div className="sidebar-brand">
            <span className="sidebar-brand-title">TULSIAN GROUP</span>
            <span className="sidebar-brand-subtitle">{isSuperAdmin ? 'Super Admin Dashboard' : (isAdmin ? roleLabel : 'Employee Dashboard')}</span>
          </div>
          <button className="drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close menu">
            <Icon name="close" size={20} />
          </button>
        </div>

        <div className="sidebar-divider" />

        {/* User card — no status labels */}
        <div className="sidebar-user-card" onClick={() => { clearSelection(); handleNav(isSuperAdmin ? '/home' : '/profile'); }}>
          <div className="sidebar-avatar">
            <span>{initials}</span>
          </div>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{fullName}</span>
            <span className="sidebar-user-meta">{isAdmin ? roleLabel : ''}</span>
          </div>
        </div>

        <div className="sidebar-divider" />

        <nav className="sidebar-nav">
          {isSuperAdmin ? (
            <div className="sidebar-nav-zone">
              <span className="sidebar-zone-label">Super Admin Zone</span>
              {superAdminNavItems.map((item) => (
                <button
                  key={item.path}
                  className={`sidebar-nav-item ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => handleNav(item.path)}
                >
                  <span className="sidebar-nav-icon">
                    <Icon name={item.icon} size={20} />
                  </span>
                  <span className="sidebar-nav-text">{item.label}</span>
                </button>
              ))}
            </div>
          ) : null}

          <div className="sidebar-nav-zone">
            {isAdmin ? <span className="sidebar-zone-label">Common Zone</span> : null}
            {commonNavItems.map((item) => (
              <button
                key={item.path}
                className={`sidebar-nav-item ${location.pathname === item.path ? 'active' : ''}`}
                onClick={() => handleNav(item.path)}
              >
                <span className="sidebar-nav-icon">
                  <Icon name={item.icon} size={20} />
                </span>
                <span className="sidebar-nav-text">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>

        <div className="sidebar-spacer" />

        <div className="sidebar-footer">
          <div className="sidebar-divider" />
          <button className="sidebar-nav-item" onClick={openChangePw}>
            <span className="sidebar-nav-icon">
              <Icon name="lock" size={20} />
            </span>
            <span className="sidebar-nav-text">Change Password</span>
          </button>
          <button className="sidebar-nav-item sidebar-logout" onClick={handleLogout}>
            <span className="sidebar-nav-icon">
              <Icon name="logout" size={20} />
            </span>
            <span className="sidebar-nav-text">Logout</span>
          </button>
        </div>
      </aside>

      {/* Change Password Modal */}
      {showChangePw && (
        <AppModal
          title="Change Password"
          subtitle={pwTarget ? `for ${pwTarget.label}` : ''}
          onClose={() => setShowChangePw(false)}
          contentClassName="cpw-modal"
          bodyClassName="cpw-body"
        >
            <form className="cpw-form" onSubmit={handleChangePw}>
              {pwError && <div className="cpw-error">{pwError}</div>}
              {pwSuccess && <div className="cpw-success">{pwSuccess}</div>}
              <div className="cpw-field">
                <label className="cpw-label">New Password</label>
                <div className="cpw-input-wrap">
                  <input
                    type={pwVisible.new ? 'text' : 'password'}
                    className="cpw-input"
                    value={pwForm.new_password}
                    onChange={(e) => setPwForm((f) => ({ ...f, new_password: e.target.value }))}
                    placeholder="Enter new password"
                    autoComplete="new-password"
                  />
                  <button type="button" className="cpw-eye" onClick={() => setPwVisible((v) => ({ ...v, new: !v.new }))} tabIndex={-1}>
                    <Icon name={pwVisible.new ? 'eyeOff' : 'eye'} size={16} />
                  </button>
                </div>
              </div>
              <div className="cpw-field">
                <label className="cpw-label">Confirm New Password</label>
                <div className="cpw-input-wrap">
                  <input
                    type={pwVisible.confirm ? 'text' : 'password'}
                    className="cpw-input"
                    value={pwForm.confirm_password}
                    onChange={(e) => setPwForm((f) => ({ ...f, confirm_password: e.target.value }))}
                    placeholder="Confirm new password"
                    autoComplete="new-password"
                  />
                  <button type="button" className="cpw-eye" onClick={() => setPwVisible((v) => ({ ...v, confirm: !v.confirm }))} tabIndex={-1}>
                    <Icon name={pwVisible.confirm ? 'eyeOff' : 'eye'} size={16} />
                  </button>
                </div>
              </div>
              <div className="cpw-actions modal-actions app-modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowChangePw(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={pwSaving}>
                  {pwSaving ? 'Saving...' : 'Change Password'}
                </button>
              </div>
            </form>
        </AppModal>
      )}
    </>
  );
}
